import React, { useState } from 'react';
import { supabase } from '../supabase';
import { useUser } from '../context/UserContext';
import Flag from './Flag';
import { AppleHelp } from './NotifyPreferences';
import { isPushSupported, permission, subscribe } from '../push';
import { pick, loginText, notifyText } from '../i18n';

const LANGS = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'zh', name: '繁體' },
  { code: 'sc', name: '简体' },
];


export default function LoginModal({ onLangChange }) {
  const { login } = useUser();
  const [lang, setLang] = useState(
    () => localStorage.getItem('bibleAppLang') || 'en'
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [wantEmail, setWantEmail] = useState(true);
  const [wantPush, setWantPush] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoverMsg, setRecoverMsg] = useState('');
  // Set only when Google has verified an address that has no account yet, so
  // the form below is finishing a signup rather than starting one.
  const [googleEmail, setGoogleEmail] = useState('');
  const [returning, setReturning] = useState(false);

  const t = pick(loginText, lang);
  const nt = pick(notifyText, lang);   // shared with the account menu

  function switchLang(code) {
    setLang(code);
    localStorage.setItem('bibleAppLang', code);
    if (onLangChange) onLangChange(code);
  }

  // Coming back from Google. The address Google returns is verified, so it is
  // enough on its own to reach an existing account — no name typed, no history
  // lost, nothing to re-register. Only a reader with no account yet has to
  // stop and pick a name.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      const authUser = data?.session?.user;
      if (!authUser?.email || cancelled) return;
      setReturning(true);

      // The account is looked up on the server from the session token itself,
      // so the browser never gets to name the address it wants.
      const addr = authUser.email.toLowerCase();
      const { data: found, error: lookupError } = await supabase.rpc('google_account');
      if (cancelled) return;
      if (lookupError) console.error('google account lookup failed', lookupError);

      if (found?.status === 'ok') {
        login({
          id: found.id, name: found.name,
          email: found.email, isAdmin: found.is_admin || false,
        });
        return;
      }

      // Nobody here yet. Suggest the Google profile name, but let them change
      // it — it is what the whole congregation will see on the leaderboard.
      const meta = authUser.user_metadata || {};
      setGoogleEmail(addr);
      setEmail(addr);
      setName(String(meta.full_name || meta.name || '').trim());
      setReturning(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signInWithGoogle() {
    setError('');
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (authError) {
      console.error('google sign-in failed', authError);
      setError(t.errorGeneric);
    }
  }

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName || !trimmedEmail) { setError(t.errorBoth); return; }
    // The same full shape sign_in() requires: an address like "name@msn.co."
    // once stopped the daily email for everyone, so it is caught here first.
    if (!/^[a-z0-9._%+-]+@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(trimmedEmail)) {
      setError(t.errorEmail);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // The server resolves the typed name to an account: the account's own
      // name first, then any name it used to go by, so merged accounts keep
      // working — "Ayele" still reaches the account now called "Ayele Dodoo".
      // Failing both, an address that already has an account signs into it and
      // the typed name is remembered. One address, one account — this is what
      // stops a second account appearing every time someone abbreviates.
      // It also records the login time and the daily email choice.
      const { data: result, error: signInError } = await supabase.rpc('sign_in', {
        p_name: trimmedName, p_email: trimmedEmail, p_daily_email: wantEmail,
      });
      if (signInError) throw signInError;

      if (result?.status === 'name_taken') {
        setError(t.errorTaken);
        setLoading(false);
        return;
      }
      if (result?.status !== 'ok') throw new Error('unexpected sign-in result');

      await askForPush(result.id, result.email);
      login({ id: result.id, name: result.name, email: result.email, isAdmin: result.is_admin || false });
    } catch (err) {
      console.error(err);
      setError(t.errorGeneric);
    }

    setLoading(false);
  };

  // The daily email choice is saved by sign_in itself. Push is asked for here,
  // after the account exists; the prompt is still inside the click that
  // submitted the form, which is what browsers require before they will ask.
  async function askForPush(accountId, accountEmail) {
    try {
      if (wantPush && isPushSupported() && permission() !== 'denied') {
        await subscribe(accountEmail, accountId);
      }
    } catch (err) {
      console.error('could not save notification preferences', err);
    }
  }

  async function recoverName() {
    const addr = email.trim().toLowerCase();
    if (!addr.includes('@')) { setRecoverMsg(t.recoverNeedEmail); return; }
    setRecovering(true); setRecoverMsg('');
    try {
      await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/recover-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addr }),
      });
    } catch (err) {
      console.error('recover-name request failed', err);
    }
    // Deliberately the same message either way — see the function's comment.
    setRecoverMsg(t.recoverSent);
    setRecovering(false);
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        {/* Language switcher */}
        <div style={styles.langRow}>
          {LANGS.map(({ code }) => (
            <button
              key={code}
              onClick={() => switchLang(code)}
              style={{
                ...styles.langBtn,
                background: lang === code ? '#3b6fd4' : 'transparent',
                color: lang === code ? '#fff' : 'var(--text, #aaa)',
                border: lang === code ? '1px solid #3b6fd4' : '1px solid var(--border, #444)',
              }}
            >
              <Flag code={code} />
            </button>
          ))}
        </div>

        <h2 style={styles.title}>{t.title}</h2>
        <p style={styles.subtitle}>{t.subtitle}</p>
        <p style={styles.desc}>
          {googleEmail
            ? t.googleFinish
            : t.desc.split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}
        </p>

        {/* Offered first, because it is the shorter path for anyone who has an
            account already — the verified address finds them without a name. */}
        {!googleEmail && (
          <>
            <button className="login-google" onClick={signInWithGoogle} disabled={returning}>
              <GoogleMark />
              <span>{t.google}</span>
            </button>
            <div className="login-or"><span>{t.orDivider}</span></div>
          </>
        )}

        <input
          style={styles.input}
          type="text"
          placeholder={t.namePlaceholder}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        {googleEmail ? (
          <div className="login-verified">
            {t.googleSignedIn} <b>{googleEmail}</b>
          </div>
        ) : (
          <input
            style={styles.input}
            type="email"
            placeholder={t.emailPlaceholder}
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        )}

        {/* Opt-ins, offered once at the start rather than as a prompt later.
            Both are changeable afterwards under the account name. */}
        <div className="login-notify">
          <label className="login-check">
            <input type="checkbox" checked={wantEmail}
                   onChange={e => setWantEmail(e.target.checked)} />
            <span>{nt.email}</span>
          </label>
          {isPushSupported() && permission() !== 'denied' && (
            <label className="login-check">
              <input type="checkbox" checked={wantPush}
                     onChange={e => setWantPush(e.target.checked)} />
              <span>{nt.push}</span>
            </label>
          )}
          <AppleHelp t={nt} />
        </div>

        {/* Anyone who has forgotten their name has their address; that is
            enough to remind them without exposing whether it is registered. */}
        {!googleEmail && (
          <div style={{ width: '100%', textAlign: 'left' }}>
            <button className="login-forgot" onClick={recoverName} disabled={recovering}>
              {recovering ? t.recoverSending : t.recoverAsk}
            </button>
            {recoverMsg && <div className="login-forgot-msg">{recoverMsg}</div>}
          </div>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button
          style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? t.loading : t.button}
        </button>
      </div>
    </div>
  );
}

// Google requires its own mark on the button, at its own colours — an emoji or
// a recoloured glyph would not meet their branding terms.
function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.2-.4-4.7H24v8.9h11.8c-.5 2.7-2 5-4.4 6.6v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.3z" />
      <path fill="#34A853" d="M24 46c6 0 11-2 14.6-5.4l-7.1-5.5c-2 1.3-4.5 2.1-7.5 2.1-5.8 0-10.7-3.9-12.4-9.1H4.3v5.7C7.9 41 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.6 28.1c-.4-1.3-.7-2.7-.7-4.1s.2-2.8.7-4.1v-5.7H4.3C2.8 17.1 2 20.4 2 24s.8 6.9 2.3 9.8l7.3-5.7z" />
      <path fill="#EA4335" d="M24 10.8c3.3 0 6.2 1.1 8.5 3.3l6.3-6.3C35 4.1 30 2 24 2 15.4 2 7.9 7 4.3 14.2l7.3 5.7c1.7-5.2 6.6-9.1 12.4-9.1z" />
    </svg>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    background: 'var(--bg, #fff)',
    color: 'var(--text, #222)',
    borderRadius: '16px',
    padding: '32px 32px 36px',
    width: '100%',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
  },
  langRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '4px',
  },
  langBtn: {
    padding: '5px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.15s',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 700,
    textAlign: 'center',
  },
  subtitle: {
    margin: 0,
    fontSize: '13px',
    opacity: 0.6,
  },
  desc: {
    margin: '4px 0',
    fontSize: '14px',
    textAlign: 'center',
    opacity: 0.8,
    lineHeight: 1.6,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid var(--border, #ddd)',
    fontSize: '15px',
    background: 'var(--input-bg, #f9f9f9)',
    color: 'var(--text, #222)',
    boxSizing: 'border-box',
  },
  error: {
    color: '#e53e3e',
    fontSize: '13px',
    margin: 0,
    textAlign: 'center',
  },
  button: {
    marginTop: '4px',
    width: '100%',
    padding: '13px',
    borderRadius: '8px',
    border: 'none',
    background: '#3b6fd4',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};